import React from 'react';
import PropTypes from 'prop-types';
import NumberWithIndicator from '../../numberWithIndicator/NumberWithIndicator';
import WidgetErrorMessage from '../../../WidgetErrorMessage';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../../utils/wrapperUtils';
import { getErrorMessageKey } from '../../../utils/dataFormatUtils';
import {
    getIndicatorStatus,
    getIndicatorColorFromStatus,
} from '../../numberWithIndicator/numberWithIndicatorUtils';
import { noDataIdentifier } from '../../../../../shared/constants/constants';

/* eslint-disable complexity */
function getThreshold(thresholdConfig, metricSeries) {
    if (!thresholdConfig) {
        return {
            thresholdErrorKey: 'invalidThresholdConfig',
        };
    }

    return {
        threshold: {
            value: metricSeries?.[thresholdConfig.value],
            stringValue: metricSeries?.[thresholdConfig.stringValue],
            colorWhenGood:
                metricSeries?.[thresholdConfig.colorWhenGood] ?? false,
            colorWhenBad: metricSeries?.[thresholdConfig.colorWhenBad] ?? false,
            expectValueOverThreshold:
                metricSeries?.[thresholdConfig.expectValueOverThreshold] ??
                false,
        },
    };
}

function getValue(valueConfig, metricSeries) {
    return {
        value: {
            value: metricSeries?.[valueConfig.value],
            stringValue: metricSeries?.[valueConfig.stringValue],
        },
    };
}

function getFootnoteLabel(footnoteLabelConfig, metricSeries) {
    if (footnoteLabelConfig) {
        return {
            footnoteLabel: {
                value: metricSeries?.[footnoteLabelConfig.value],
                labelKey: metricSeries?.[footnoteLabelConfig.labelKey],
            },
        };
    }
    return {
        footnoteLabel: {
            value: '',
            labelKey: '',
        },
    };
}

function getMetricSource(metricRequests, metrics) {
    if (metricRequests.length !== 1) {
        return {
            errorKey: 'invalidMetricRequests',
        };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return {
            errorKey: 'metricSourceNotFound',
        };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return {
            errorKey: 'metricSourceUnauthorized',
        };
    }

    if (!hasMetricSources([metricSource], true)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}

export default function StringWithIndicatorWrapper(props) {
    const {
        linkConfig,
        textAlign = 'inherit',
        thresholdConfig,
        valueConfig = {},
        footnoteLabelConfig = {},
    } = props.configuration;

    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props.metricRequests, props.metrics);

    const { threshold, thresholdErrorKey } = getThreshold(
        thresholdConfig,
        metricSource.metricSeries[0]
    );

    const { value } = getValue(valueConfig, metricSource.metricSeries[0]);

    const { footnoteLabel } = getFootnoteLabel(
        footnoteLabelConfig,
        metricSource.metricSeries[0]
    );

    const errorMessageKey = getErrorMessageKey(errorKey, thresholdErrorKey);
    if (errorMessageKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorMessageKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const formattedLabel = Object.keys(props.customLabels).length
        ? `${props.customLabels[footnoteLabel.labelKey]} ${footnoteLabel.value}`
        : `${footnoteLabel.labelKey} ${footnoteLabel.value}`;

    const indicatorStatus = getIndicatorStatus(
        value.value,
        threshold.value,
        threshold.expectValueOverThreshold
    );

    const indicatorColor = getIndicatorColorFromStatus(
        indicatorStatus,
        threshold.colorWhenBad,
        threshold.colorWhenGood
    );

    return (
        <NumberWithIndicator
            footnoteLabel={formattedLabel}
            textAlign={textAlign}
            indicatorColor={indicatorColor}
            linkConfig={linkConfig}
            metricRequest={metricRequest}
            openLinksInNewTab={props.openLinksInNewTab}
            threshold={threshold.stringValue ?? noDataIdentifier}
            value={value.stringValue}
        />
    );
}
/* eslint-enable complexity */

StringWithIndicatorWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    customLabels: PropTypes.instanceOf(Object),
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    metricRequests: PropTypes.instanceOf(Array).isRequired,
    metrics: PropTypes.instanceOf(Array).isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
};

StringWithIndicatorWrapper.defaultProps = {
    customLabels: {},
};
